import { debounce, forIn, groupBy, isEqual, isNil, sortBy, toNumber } from "lodash";
import { lazy, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Col, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { IconDelete, IconGift, IconRefresh } from "../../../_metronic/css/icons";
import ApiAmusementService from "../../../core/api/api.amusementService";
import ContextApp from "../../../core/contexts/context.app";
import { BasketHelper } from "../../../core/utils/BasketHelper";
import SplitNumber from "../_Components/SplitNumber";
import Sw2 from "sweetalert2";
import ServiceApi from "../../../core/services/service.api";
import { tools } from "../../../core/utils/tools";
import SwalMessage from "../_Components/SwalMessage";

const SvgContainer = lazy(() => import(/* webpackChunkName: "SvgContainer" */ "./SvgContainer"));

const HallEvent = ({ hallEvents, serviceInfo, setEvents, selectedEventTime, selectedDate, basket }) => {
  const ctx = useContext(ContextApp);
  const navigate = useNavigate()
  const controller = useRef()
  const [loadingSvg, setLoadingSvg] = useState(true)
  const [getJsonLoading, setGetJsonLoading] = useState(false)
  const [addBasketLoading, setAddBasketLoading] = useState(false)
  const [pricedSeats, setPricedSeats] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedDiffrentWithBasket, setSelectedDiffrentWithBasket] = useState(false)
  const [basketSelectedIds, setBasketSelectedIds] = useState([])
  const [showSvg, setShowSvg] = useState("")
  const [openChangeToast, setOpenChangeToast] = useState(false)
  const svgContianer = useRef()
  const firstMount = useRef(true)
  const setSvgJson = useMemo(() => debounce(async () => {
    controller.current?.abort()
    const newController = new AbortController()
    controller.current = newController
    try {
      setLoadingSvg(true)
      setPricedSeats([])
      const svgStr = await ServiceApi.get(
        `${process.env.REACT_APP_PHOTOPATH_WEB}/${selectedEventTime.svgFilePath}`,
        { signal: controller.current?.signal }
      )

      // if (!firstMount.current) {
      //   await getSeatJson()
      // }
      // firstMount.current = false
      if (controller.current == newController) {
        setLoadingSvg(false)
        setShowSvg(svgStr ?? "")
        setPricedSeats(JSON.parse(selectedEventTime.seatPriceStatusJson))
      }
    } catch {
      if (controller.current == newController)
        setLoadingSvg(false)
    }
  }, 250), [firstMount.current, selectedEventTime])

  const getSeatJson = useCallback(async () => {
    setGetJsonLoading(true)
    const response =
      await ApiAmusementService.getReserveDetails({
        urlName: serviceInfo.urlName,
      });
    if (!response.succeeded)
      SwalMessage({ iSsuccess: false, title: response.message })
    const responseData = response?.data
    const maped = groupBy(responseData?.hallEvents, "eventDate")
    for (const date in maped) {
      const rawGrouped = groupBy(maped[date], "executionTimeStr")
      Object.keys(rawGrouped).forEach((time) => rawGrouped[time] = rawGrouped[time][0])
      maped[date] = rawGrouped
    }
    setEvents(maped)
    const newHallEvent = responseData.hallEvents?.find(item => item.eventDate == selectedDate?.date && item.id == selectedEventTime?.id)

    const seatsJson = JSON.parse(newHallEvent.seatPriceStatusJson)
    setPricedSeats(seatsJson)
    for (const seat of selectedIds) {
      const foundedPriced = seatsJson.find(item => item.sId == +seat?.sId)
      const foundedBasket = basketSelectedIds.includes(+seat?.sId)
      if (foundedPriced.st != 1) {
        if (foundedBasket && foundedPriced.st == 3) {
          delete seat?.isHoverAdd
          continue
        }
        if (foundedBasket) {
          const basketItem = basket.basketItemTemps.find(item => item.hallEventId == selectedEventTime.id)
          const basketItemPerson = basketItem.basketItemPersonTemps.find(item => item.hallSeatId == seat?.sId)
          await BasketHelper.deleteBasketItemPerson(ctx, basketItem, basketItemPerson)
        }
      }
    }
    setGetJsonLoading(false)
    return { seatsJson, newHallEvent }
  }, [basketSelectedIds, selectedEventTime, selectedIds, selectedDate])

  useEffect(() => {
    if (selectedEventTime)
      (async () => {
        try {
          await setSvgJson()
          setPricedSeats(JSON.parse(hallEvents[selectedEventTime.eventDate][selectedEventTime.executionTimeStr].seatPriceStatusJson))
        } catch {
        }
      })()
  }, [selectedEventTime])

  useEffect(() => {
    setSelectedIds([])
    if (showSvg && basket) {
      const basketSeatsId = []
      const selected = []

      basket.basketItemTemps.filter(bi => !bi.isDeleted && bi.hallEventId == selectedEventTime.id).forEach(basketItem => {
        basketItem.basketItemPersonTemps.forEach(basketItemPerson => {
          const foundedSeat = pricedSeats.find(item => item.sId == basketItemPerson.hallSeatId)
          const isInTheBasket = foundedSeat?.st == 1
          if (!!foundedSeat && !isInTheBasket) BasketHelper.deleteBasketItemPerson(ctx, basketItem, basketItemPerson)
          else if (basketItem.checkInDate == selectedEventTime?.eventDate)
            basketSeatsId.push(basketItemPerson.hallSeatId)
        })
      })
      basketSeatsId.forEach(seatId => {
        const element = svgContianer.current.querySelector(`g[sNu='${seatId}']`)
        if (element) {
          selected.push({ sId: element.getAttribute("snu"), sName: element.getAttribute("sna"), rName: element.getAttribute("rna"), bName: element.getAttribute("bna") })
        }
      })

      setSelectedIds(prev => {
        const old = [...prev, ...selected]
        let newMap = new Map(old.map(item => [+item.sId, item]))
        while (newMap.size > 10) {
          old.shift()
          newMap = new Map(prev.map(item => [+item.sId, item]))
        }
        return [...newMap.values()]
      })
      setBasketSelectedIds(basketSeatsId)
    }
  }, [showSvg, basket, selectedDate, pricedSeats])
  const debouncedSetShowDiffrentAlert = debounce(() => {
    const selectedItemFromSelectedIds = selectedIds.filter(item => !item.isHoverDelete && !item.isHoverAdd).map(seat => basketSelectedIds.includes(+seat?.sId))
    if (!selectedItemFromSelectedIds.every(Boolean) || selectedIds.filter(item => !item.isHoverDelete).length != basketSelectedIds.filter(item => !!item).length) {
      setSelectedDiffrentWithBasket(true)
    }
    else setSelectedDiffrentWithBasket(false)
  }, 300)

  useEffect(() => {
    debouncedSetShowDiffrentAlert()
  }, [selectedIds, basketSelectedIds])

  useEffect(() => {
    if (selectedDiffrentWithBasket)
      if (!openChangeToast)
        toast("برای ثبت تغییرات سبد خرید حتما روی گزینه به روزرسانی کلیک نمایید", {
          position: "top-right",
          onOpen: () => {
            setOpenChangeToast(true)
          },
          onClose: () => {
            setOpenChangeToast(false)
          },
        })
  }, [selectedDiffrentWithBasket])

  const updateSeatsJsonAndCheck = useCallback(async () => {
    const { seatsJson, newHallEvent } = await getSeatJson()
    const selectedIdsCanAddToBasket = selectedIds.filter(item => !item.isHoverDelete).map(selected => {
      const founded = seatsJson.find(seat => seat?.sId == selected.sId)
      const can = founded.st == 1 && !!founded.oP && !!founded.sP
      return can
    })
    const problemSeats = selectedIdsCanAddToBasket.filter(item => !item).map((item, index) => selectedIds.filter(item => !item.isHoverDelete).find(seattt => seattt.sId == selectedIds[index].sId))
    setSelectedIds(prev => prev.filter(item => !item.isHoverDelete))
    return problemSeats
  }, [getSeatJson, basket, selectedIds, basketSelectedIds])

  const addToBasket = useCallback(async () => {
    setAddBasketLoading(true)
    let sumOriginalPrice = 0
    let sumPrice = 0
    let CommissionAmounts = 0
    let AgentPaidAmounts = 0
    const details = []
    const personsList = []
    const toastSetting = {
      autoClose: 2500,
      closeOnClick: true,
      position: "top-right",
      toastId: `${Date.now()}`,
    };
    details.push({ type: "event", id: selectedEventTime.id, text: selectedEventTime.title })

    const problemSeats = await updateSeatsJsonAndCheck()
    const problemSeatsIds = problemSeats.map(item => item.sId)

    selectedIds.filter(item => !item.isHoverDelete).filter(item => !problemSeatsIds.includes(item.sId)).forEach(seatInfo => {
      const priced = pricedSeats.find(seat => seat?.sId == seatInfo.sId)
      sumOriginalPrice += priced.oP
      sumPrice += priced.sP
      CommissionAmounts += priced.CommissionAmount
      AgentPaidAmounts += priced.AgentPaidAmount
      personsList.push({
        basketPersonPersonFullName: " ",
        hallSeatId: +seatInfo.sId,
        originalPrice: priced.oP,
        salePrice: priced.sP,
        price: priced.sP,
        commissionAmount: priced.CommissionAmount,
        agentPaidAmount: priced.AgentPaidAmount,
        hallBlockName: seatInfo.bName,
        hallRowName: seatInfo.rName,
        hallSeatName: seatInfo.sName,
      })
    })

    const toastContent = (
      <div>
        صندلی های زیر <b>غیرقابل خرید</b> می‌باشد:
        {problemSeats.map(item => `* جایگاه: ${item.bName} - ردیف: ${item.rName} - صندلی: ${item.sName}`).map((item, index) => <div key={index} className="mt-1">
          {item}
          {index != problemSeats.length - 1 && <div style={{ height: 1, backgroundColor: "black" }} />}
        </div>)}
      </div>
    )
    const sweetAlertModal = Sw2.mixin({
      customClass: {
        confirmButton: "btn btn-success px-5 py-2 mr-2",
        cancelButton: "btn btn-outline-success py-2  px-5",
        htmlContainer: "mx-3"
      },
      buttonsStyling: false,
    });

    await BasketHelper.addItemToBasket(
      ctx,
      toNumber(selectedIds.filter(item => !item.isHoverDelete).filter(item => !problemSeatsIds.includes(item.sId)).length),
      sumOriginalPrice, //toString(sumOriginalPrice),
      sumPrice, //toString(sumPrice),
      serviceInfo.id,
      serviceInfo.name,
      serviceInfo.imageUrl,
      selectedDate.date,
      JSON.stringify(details),
      //await BasketHelper.addBasketPersonList(ctx, personsList),
      personsList,
      null,
      null,
      AgentPaidAmounts,
      CommissionAmounts,
      0,
      0,
      selectedEventTime.id,
      selectedEventTime.executionTimeStr,
    );

    await getSeatJson()

    const seatsss = selectedIds.filter(item => !item.isHoverDelete).filter(item => !problemSeatsIds.includes(item.sId)).map(seatInfo => {
      const seat = pricedSeats.find(seat => seat?.sId == seatInfo.sId)
      return `
          <div
            class="item-box-row selected-item mt-2 flex-shrink-0 mb-3 border"
          >
            <div
              class="row pe-3 ps-1 fw-bold position-relative"
            >
              <div style='width: 40px; height: 40px; border-radius: 100%; top: -22px; left: 45%;' class="position-absolute bg-white" ></div>
              <div style='width: 40px; height: 40px; border-radius: 100%; bottom: -22px; left: 45%;' class="position-absolute bg-white" ></div>
              <div style="max-width:${window.innerWidth < 1200 ? "157px" : "182px"};" class="col-7 text-end fs-08 p-1">
                جایگاه : ${seatInfo.bName}
                <br />
                ردیف : ${seatInfo.rName}
                <br />
                صندلی : ${seatInfo.sName}
              </div>
              <div class="col-auto flex-grow-1 fs-08 my-auto text-end pe-0">
            <div class="row p-0 m-0 justify-content-end">
              <div class="col p-0 m-0 fs-075">نرخ فروش: </div>
              <div class="col d-flex p-0 m-0 gap-1 justify-content-end">
                <span class="fw-bold fs-075">
                  ${tools.formatPriceNumber(seat?.sP ?? 0)}
                </span>
                <span class="fs-075">تومان</span>
              </div>
            </div>
            <div class="row p-0 m-0 justify-content-end">
              <div class="col p-0 m-0 fs-075">کمیسیون: </div>
              <div class="col d-flex p-0 m-0 gap-1 justify-content-end">
                <span class="fw-bold fs-075">
                  ${tools.formatPriceNumber(seat?.CommissionAmount ?? 0)}
                </span>
                <span class="fs-075">تومان</span>
              </div>
            </div>
            <div class="row text-dark-blue p-0 m-0 justify-content-end">
              <div class="col p-0 m-0 fs-075">خالص پرداختی: </div>
              <div class="col d-flex p-0 m-0 gap-1 justify-content-end">
                <span class="fw-bold fs-075">
                  ${tools.formatPriceNumber(seat?.AgentPaidAmount ?? 0)}
                </span>
                <span class="fs-075">تومان</span>
              </div>
            </div>
              </div>
            </div>
          </div> `
    }).join("")
    if (selectedIds.filter(item => !item.isHoverDelete).filter(item => !problemSeatsIds.includes(item.sId)).length)
      sweetAlertModal
        .fire({
          title: " آیتم های زیر به سبد خرید اضافه شد.",
          showClass: {
            popup: `
            animate__animated
            animate__fadeIn
            animate__faster            `,
          },
          hideClass: {
            popup: `
            animate__animated
            animate__fadeOut
            animate__faster            `,
          },
          icon: "success",
          html: `<div class='border shadow-sm rounded-3 p-2'>
                    <div class='d-flex align-items-center justify-content-start border-bottom pb-2 mb-1 w-100'>
                        <img src=${process.env.REACT_APP_PHOTOPATH_WEB + serviceInfo.imageUrl} class='rounded-3 ' alt='ddd' width='75'/>
                        <div class='pe-2 w-100'>
                            <div class='fs-09 fw-bold text-end'>${serviceInfo.name}</div>
                            <div class='fs-09 fw-bold text-end'>${selectedEventTime.title}</div>
                            <div class='d-flex align-items-center justify-content-between w-100 mt-1'>
                              <div class='d-flex align-items-center'>
                                <div class='fs-075'>تاریخ استفاده :</div>
                                <div class='fs-075 fw-bold'>
            ${selectedEventTime.eventDate}
                                </div>
                              </div>
                              <div class='d-flex align-items-center'>
                                <div class='fs-075'>ساعت :</div>
                                <div class='fs-075 fw-bold'>
            ${selectedEventTime.executionTimeStr}
                                </div>
                              </div>
                            </div>
                        </div>  
                    </div>
                    <div class='container overflow-hidden px-0 mt-2 '>
    ${seatsss}
                    </div>
                </div >    
              </div > `,
          type: "success",
          showConfirmButton: true,
          preConfirm: () => {
            navigate("/basket");
          },
          confirmButtonText: "   مشاهده سبد خرید  ",
          showCancelButton: true,
          cancelButtonText: "   اضافه کردن سایر تفریحات ",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            navigate("/basket");
          }
        })
    else
      sweetAlertModal.fire({
        icon: !selectedIds.length ? "warning" : "success",
        text: !selectedIds.length ? "ابتدا صندلی های مورد نظر خود را انتخاب کنید" : "صندلی ها از سبد خرید حذف شد",
        timer: 1500,
        showConfirmButton: false,
      });

    if (problemSeats.length) toast.warning(toastContent, toastSetting)
    setAddBasketLoading(false)
  }, [ctx, getSeatJson, pricedSeats, selectedIds, selectedEventTime, updateSeatsJsonAndCheck])

  const removeTicketFromSelect = useCallback(async (seat) => {
    const seatInfo = selectedIds.find(item => item.sId == seat?.sId)
    const toastSetting = {
      autoClose: 2500,
      closeOnClick: true,
      position: "top-right",
      toastId: `${seat?.sId} -${Date.now()} `,
    };

    const toastContent = (
      <>
        <div>حذف شد</div>
        <div>
          <span> جایگاه {seatInfo.bName} - </span>
          <span> ردیف {seatInfo.rName} - </span>
          <span>صندلی {seatInfo.sName} - </span>
          <span className="fw-bold">
            قیمت {tools.formatPriceNumber(seat?.sP)} تومان
          </span>
        </div>
      </>
    );
    toast.error(toastContent, toastSetting);
    setSelectedIds(prev => {
      const copyArr = [...prev]
      const index = copyArr.findIndex(item => item.sId == seat?.sId)
      const isInBasket = basketSelectedIds.includes(+seat?.sId)

      if (isInBasket)
        copyArr[index].isHoverDelete = true
      else return copyArr.filter(item => item.sId != seat?.sId)
      return copyArr
    })
  }, [selectedIds, basketSelectedIds, basket, updateSeatsJsonAndCheck])

  return <div
    className="mt-3 p-0"
    style={{
      pointerEvents: "auto",
    }}
  >
    <div style={{ padding: "20px" }} className="h-100 d-flex flex-column pt-0">
      <Row>
        <Col xs="12" xl="8" className="position-relative" style={{ color: "gray" }}>
          {selectedEventTime &&
            <div style={{ minHeight: 140 }} ref={svgContianer}>
              {showSvg &&
                <SvgContainer
                  onSelect={setSelectedIds}
                  svgContent={showSvg}
                  pricedSeats={pricedSeats}
                  selectedIds={selectedIds}
                  svgMaxZoomLevel={+selectedEventTime.svgMaxZoomLevel}
                  basketSelectedIds={basketSelectedIds}
                />
              }
              {loadingSvg &&
                <div style={{ inset: -1, zIndex: 11 }} className="position-absolute d-flex align-items-center justify-content-center bg-white opacity-60">
                  <Spinner style={{ width: "10cqw", height: "10cqw" }} size="xl" />
                </div>
              }
            </div>}
          {window.innerWidth < 1200 && selectedEventTime && showSvg && <div
            style={{
              bottom: 5,
              left: 5,
              padding: "3px 6px",
              marginTop: 2,
              background: "rgba(255,255,255,0.8)",
              fontSize: "12px",
            }}
          >
            <div style={{ fontSize: window.innerWidth <= 768 && 10 }} className="d-flex gap-2 justify-content-around">
              <div className="d-flex gap-1">
                <div className="my-auto flex-shrink-0" style={{ width: window.innerWidth < 768 ? 15 : 20, height: window.innerWidth < 768 ? 15 : 20, backgroundColor: "#e2e2e2", borderRadius: window.innerWidth < 768 ? "100%" : 4 }} />
                        قابل رزرو
                      </div>
              <div className="d-flex gap-1">
                <div className="my-auto flex-shrink-0" style={{ width: window.innerWidth < 768 ? 15 : 20, height: window.innerWidth < 768 ? 15 : 20, backgroundColor: "#ffa08f", borderRadius: window.innerWidth < 768 ? "100%" : 4 }} />
                        رزرو شده
                      </div>
              <div className="d-flex gap-1">
                <div className="my-auto flex-shrink-0" style={{ width: window.innerWidth < 768 ? 15 : 20, height: window.innerWidth < 768 ? 15 : 20, backgroundColor: "#e4d98a", borderRadius: window.innerWidth < 768 ? "100%" : 4 }} />
                        در انتظار پرداخت
                      </div>
              <div className="d-flex gap-1">
                <div className="my-auto flex-shrink-0" style={{ width: window.innerWidth < 768 ? 15 : 20, height: window.innerWidth < 768 ? 15 : 20, backgroundColor: "#7ad3aa", borderRadius: window.innerWidth < 768 ? "100%" : 4 }} />
                        در سبد شما
                      </div>
            </div>
          </div>}
        </Col>
        {selectedEventTime && <Col xs="12" xl="4" className={`d-flex flex-column ${!window.innerWidth < 1200 && "py-5"} `}>
          {window.innerWidth < 1200 && <>
            <div className="mt-3">
              <span style={{ visibility: selectedDiffrentWithBasket ? "visible" : "hidden" }} className="text-danger fs-08"> ** حتما برای اعمال تغییرات در سبد خرید روی دکمه <span className="fw-bold text-decoration-underline">به‌روز رسانی سبد خرید</span> کلیک کنید !
                      </span>
              <button
                onClick={addToBasket}
                className="btn btn-success d-flex align-items-center w-100 p-2 btn-with-icon mt-2"
                type="button"
                disabled={getJsonLoading || addBasketLoading}
              >
                <div
                  className="border-start border-success icon-span">
                  <IconRefresh fillColor="#fff" />
                </div>
                <div className=" text-center flex-fill">
                  به‌روز رسانی سبد خرید
                        </div>
              </button>
            </div>
          </>
          }
          <div className="flex-grow-1 mt-2">
            <div
              className="items-box mt-4 mb-2"
              style={{
                pointerEvents: "auto",
              }}
            >
              <div className="items-box-header bg-white rounded-2 py-1 ps-2 pe-2 me-2 fs-085 fw-bold">
                صندلی های انتخاب شده
                      </div>
              <div style={{ left: 7, top: -12 }} className="items-box-header bg-white rounded-2 py-1 ps-2 pe-1 ms-2 fs-07 fw-bold">
                تعداد: {selectedIds.filter(item => !item.isHoverDelete).length}
              </div>
              <div style={{ padding: "15px", paddingLeft: "10px", height: 330, scrollbarWidth: "thin", scrollbarColor: "#999 #EEE" }} className="item-box-body d-flex flex-column overflow-auto">
                {selectedIds.map((seatInfo, idx) => {
                  const seat = pricedSeats.find(item => item.sId == +seatInfo.sId)
                  return <div
                    key={seat?.sId + `${idx}`}
                    style={{
                      borderRadius: 12,
                    }}
                    className="item-box-row px-3 position-relative selected-item mt-2 flex-shrink-0 mb-3 border"
                  >
                    {seatInfo.isHoverDelete &&
                      <div style={{ inset: 0, zIndex: 9, borderRadius: 12, backgroundColor: "rgba(255,255,255,0.8)" }} className="position-absolute fw-bold text-danger">
                        <span className="position-absolute" style={{ marginTop: 17, display: "inline-block", left: "49%", transform: "rotate(-15deg) translateX(-50%)" }}>حذف موقت</span>
                      </div>
                    }
                    {seatInfo.isHoverAdd &&
                      <div style={{ top: -10, left: "51%", transform: "translateX(-50%)", zIndex: 9 }} className="position-absolute fw-bold text-success">
                        افزودن موقت
                            </div>
                    }
                    <Row
                      className="gx-0 fw-bold position-relative"
                      style={{
                        pointerEvents: "auto"
                      }}
                    >
                      <div style={{ width: window.innerWidth < 1200 ? "5vh" : "3vw", height: window.innerWidth < 1200 ? "5vh" : "3vw", borderRadius: "100%", top: window.innerWidth < 1200 ? -27 : "-1.75vw", left: "42%" }} className="position-absolute bg-white" />
                      <div style={{ width: window.innerWidth < 1200 ? "5vh" : "3vw", height: window.innerWidth < 1200 ? "5vh" : "3vw", borderRadius: "100%", bottom: window.innerWidth < 1200 ? -27 : "-1.75vw", left: "42%" }} className="position-absolute bg-white" />
                      <Col
                        xs="1"
                        style={{ borderTopRightRadius: 12, borderBottomRightRadius: 12, maxWidth: 25 }}
                        title="حذف صندلی"
                        onClick={() => {
                          removeTicketFromSelect(seat)
                        }}
                        className="ps-0 pe-2 removeSeatTrash d-flex align-items-center justify-content-center"
                      >
                        <IconDelete fill="rgba(255,0,0,0.8)" />
                      </Col>
                      <Col className={`flex-grow-1 p-0 d-flex ${window.innerWidth < 1200 && "flex-column"}`}>
                        <Col className="d-flex align-items-center gap-2 py-3 px-2">
                          <div className={`p-1 flex-grow-1 d-flex fs-09 fw-400 ${window.innerWidth > 1200 ? "flex-column" : "justify-content-between"}`}>
                            <div>جایگاه : {seatInfo.bName}</div>
                            <div>ردیف : {seatInfo.rName}</div>
                            <div>صندلی : {seatInfo.sName}</div>
                          </div>
                        </Col>
                        {window.innerWidth < 1200 && <div style={{ height: 1, width: "90%" }} className="bg-black mx-auto mb-3" />}
                        <Col style={{ maxWidth: window.innerWidth > 1200 && 195 }} className={`fs-08 my-auto text-end ${window.innerWidth > 1200 && "pe-5"} flex-grow-1`}>
                          {seat?.oP ? (
                            <>
                              <Row className="p-0 m-0 justify-content-end">
                                <Col className="p-0 m-0">نرخ مصوب: </Col>
                                <Col className="d-flex p-0 m-0 gap-1 justify-content-end">
                                  <span className="text-decoration-line-throught-rotate border-black-6 text-black-6">
                                    <SplitNumber number={(seat?.oP) ?? 0} />
                                  </span>
                                  <span className="fs-075 text-black-6">
                                    تومان
                                </span>
                                </Col>
                              </Row>
                              <Row className="p-0 m-0 justify-content-end">
                                <Col className="p-0 m-0">نرخ فروش: </Col>
                                <Col className="d-flex p-0 m-0 gap-1 justify-content-end">
                                  <span className="fw-bold">
                                    <SplitNumber number={(seat?.sP) ?? 0} />
                                  </span>
                                  <span className="fs-075">تومان</span>
                                </Col>
                              </Row>
                              <Row className="p-0 m-0 justify-content-end">
                                <Col className="p-0 m-0">کمیسیون: </Col>
                                <Col className="d-flex p-0 m-0 gap-1 justify-content-end">
                                  <span className="fw-bold">
                                    <SplitNumber number={(seat?.CommissionAmount) ?? 0} />
                                  </span>
                                  <span className="fs-075">تومان</span>
                                </Col>
                              </Row>
                              <Row className="text-dark-blue p-0 m-0 justify-content-end">
                                <Col className="p-0 m-0">خالص پرداختی: </Col>
                                <Col className="d-flex p-0 m-0 gap-1 justify-content-end">
                                  <span className="fw-bold">
                                    <SplitNumber number={(seat?.AgentPaidAmount) ?? 0} />
                                  </span>
                                  <span className="fs-075">تومان</span>
                                </Col>
                              </Row>
                            </>
                          ) : (
                            <span className="text-danger">غیرقابل خرید</span>
                          )}
                        </Col>
                      </Col>
                    </Row>
                  </div>
                }
                )}
              </div>
            </div>
          </div>
          {window.innerWidth > 1200 && <>
            <div>
              <span style={{ visibility: selectedDiffrentWithBasket ? "visible" : "hidden" }} className="text-danger fs-08"> ** حتما برای اعمال تغییرات در سبد خرید روی دکمه <span className="fw-bold text-decoration-underline">به‌روز رسانی سبد خرید</span> کلیک کنید !
                      </span>
              <button
                onClick={addToBasket}
                className="btn btn-success d-flex align-items-center w-100 p-2 btn-with-icon mt-3"
                type="button"
                disabled={getJsonLoading || addBasketLoading}
              >
                <div
                  className="border-start border-success icon-span">
                  <IconRefresh fillColor="#fff" />
                </div>
                <div className=" text-center flex-fill">
                  به‌روز رسانی سبد خرید
                        </div>
              </button>
            </div>
          </>
          }
        </Col>
        }
      </Row>
    </div>
  </div >
}
export default memo(HallEvent)